import { useEffect, useState, useRef } from 'react';
import {
  FileText, Search, RefreshCw, ChevronLeft, ChevronRight, Eye,
  Radio, Filter, Download
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/lib/api';
import useAuthStore from '@/stores/authStore';
import { toast } from 'sonner';

// Correct API field: action like "tournament.create", "match.start", etc.
const getActionType = (action) => {
  if (!action) return 'other';
  const a = action.toLowerCase();
  if (a.includes('create') || a.includes('generate') || a.includes('invite') || a.includes('import')) return 'create';
  if (a.includes('update') || a.includes('edit') || a.includes('change') || a.includes('reset')) return 'update';
  if (a.includes('delete') || a.includes('cancel') || a.includes('disable') || a.includes('remove')) return 'delete';
  if (a.includes('start') || a.includes('finish') || a.includes('complete') || a.includes('live')) return 'live';
  if (a.includes('login') || a.includes('auth')) return 'auth';
  return 'other';
};

const ACTION_STYLE = {
  create: 'bg-[rgba(34,197,94,0.12)] text-[#22c55e] border-[rgba(34,197,94,0.3)]',
  update: 'bg-[rgba(59,130,246,0.12)] text-[#3b82f6] border-[rgba(59,130,246,0.3)]',
  delete: 'bg-[rgba(239,68,68,0.12)] text-[#ef4444] border-[rgba(239,68,68,0.3)]',
  live:   'bg-[rgba(245,197,24,0.12)] text-[#f5c518] border-[rgba(245,197,24,0.3)]',
  auth:   'bg-[rgba(139,92,246,0.12)] text-[#8b5cf6] border-[rgba(139,92,246,0.3)]',
  other:  'bg-[#1d2230] text-[#8a93a3] border-[#252a37]',
};

const ActionBadge = ({ action }) => {
  const type = getActionType(action);
  const label = action?.replace(/\./g, ' › ').replace(/_/g, ' ') || 'Unknown';
  return (
    <Badge className={`${ACTION_STYLE[type]} border text-xs capitalize whitespace-nowrap`}>
      {label}
    </Badge>
  );
};

const LOG_PAGE_SIZE = 25;

export default function AuditPage() {
  const { activeOrgSlug, user } = useAuthStore();
  const orgSlug = user?.memberships?.[0]?.slug || activeOrgSlug || 'btcc';
  const [logs, setLogs]               = useState([]);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState('');
  const [filterAction, setFilterAction] = useState('all');
  const [page, setPage]               = useState(1);
  const [total, setTotal]             = useState(0);
  const [selectedLog, setSelectedLog] = useState(null);
  const [live, setLive]               = useState(true);
  const [newCount, setNewCount]       = useState(0);
  const latestIdRef                   = useRef(null);
  const intervalRef                   = useRef(null);

  useEffect(() => {
    fetchLogs(true);
  }, [activeOrgSlug, page]);

  useEffect(() => {
    if (live) {
      intervalRef.current = setInterval(() => pollNew(), 15000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [live, activeOrgSlug]);

  const fetchLogs = async (initial = false) => {
    try {
      if (initial) setLoading(true);
      const res = await api.get(`/orgs/${orgSlug}/audit`, {
        params: { limit: LOG_PAGE_SIZE },
      });
      const data = res.data;
      const items = Array.isArray(data) ? data : data.items || [];
      setLogs(items);
      setTotal(data.total ?? items.length);
      if (items.length > 0 && initial) latestIdRef.current = items[0].id;
    } catch {
      if (initial) toast.error('Failed to load audit log');
    } finally {
      if (initial) setLoading(false);
    }
  };

  const pollNew = async () => {
    try {
      const res = await api.get(`/orgs/${orgSlug}/audit`, { params: { limit: 5 } });
      const items = Array.isArray(res.data) ? res.data : res.data.items || [];
      if (items.length > 0 && items[0].id !== latestIdRef.current) {
        const existing = new Set(logs.map(l => l.id));
        const fresh    = items.filter(i => !existing.has(i.id));
        if (fresh.length > 0) {
          setLogs(prev => [...fresh, ...prev]);
          setNewCount(c => c + fresh.length);
          latestIdRef.current = items[0].id;
        }
      }
    } catch (_) {}
  };

  const handleRefresh = () => {
    setNewCount(0);
    fetchLogs(true);
  };

  const handleExportCsv = async () => {
    try {
      toast.info('Fetching all events...');
      const res = await api.get(`/orgs/${orgSlug}/audit`, { params: { limit: 2000 } });
      const allLogs = Array.isArray(res.data) ? res.data : res.data.items || [];

      const header = ['Time', 'Actor', 'Actor ID', 'Action', 'Target Kind', 'Target ID', 'Changes'];
      const rows = allLogs.map(log => [
        log.at ? new Date(log.at).toLocaleString() : '',
        log.actor_name || 'System',
        log.actor_user_id || '',
        log.action || '',
        log.target?.kind || '',
        log.target?.id || '',
        log.after ? JSON.stringify(log.after) : '',
      ]);

      const csv = [header, ...rows]
        .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
        .join('\n');

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `btccp-audit-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${allLogs.length} events`);
    } catch {
      toast.error('Export failed');
    }
  };

  const actionOptions = [...new Set(logs.map(l => l.action?.split('.')[0]).filter(Boolean))].sort();

  const filteredLogs = logs.filter(log => {
    const actor = log.actor_name?.toLowerCase() || '';
    const act   = log.action?.toLowerCase() || '';
    const kind  = log.target?.kind?.toLowerCase() || '';
    const matchesSearch = actor.includes(search.toLowerCase()) ||
                          act.includes(search.toLowerCase()) ||
                          kind.includes(search.toLowerCase());
    const matchesFilter = filterAction === 'all' || log.action?.startsWith(filterAction);
    return matchesSearch && matchesFilter;
  });

  const totalPages = Math.max(1, Math.ceil(total / LOG_PAGE_SIZE));

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="admin-audit-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#f0f2f7] mb-1">Audit Log</h1>
          <div className="flex items-center gap-2 mt-1">
            {live && (
              <span className="flex items-center gap-1.5 text-xs text-[#22c55e]">
                <span className="w-2 h-2 rounded-full bg-[#22c55e] animate-pulse inline-block" />
                Live — polling every 15s
              </span>
            )}
            {newCount > 0 && (
              <Badge className="bg-[rgba(34,197,94,0.15)] text-[#22c55e] border border-[rgba(34,197,94,0.35)] text-xs">
                +{newCount} new
              </Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCsv}
            className="border-[#252a37] text-[#f0f2f7] hover:bg-[#161a26]"
            data-testid="audit-export-csv-button"
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setLive(l => !l)}
            className={`border-[#252a37] text-sm ${live ? 'text-[#22c55e]' : 'text-[#8a93a3]'} hover:bg-[#161a26]`}
            data-testid="audit-live-toggle"
          >
            <Radio className="h-4 w-4 mr-2" />
            {live ? 'Live On' : 'Live Off'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            className="border-[#252a37] text-[#f0f2f7] hover:bg-[#161a26]"
            data-testid="audit-refresh-button"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8a93a3]" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by user, action, or entity..."
            className="pl-10 bg-[#161a26] border-[#252a37] text-[#f0f2f7] placeholder:text-[#8a93a3]"
            data-testid="audit-search-input"
          />
        </div>
        <Select value={filterAction} onValueChange={setFilterAction}>
          <SelectTrigger className="w-[160px] bg-[#161a26] border-[#252a37] text-[#f0f2f7]">
            <Filter className="h-4 w-4 mr-2 text-[#8a93a3]" />
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent className="bg-[#161a26] border-[#252a37]">
            <SelectItem value="all" className="text-[#f0f2f7] focus:bg-[#1d2230]">All types</SelectItem>
            {actionOptions.map(a => (
              <SelectItem key={a} value={a} className="text-[#f0f2f7] focus:bg-[#1d2230] capitalize">{a}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Audit Table */}
      <Card className="bg-[#161a26] border-[#252a37]">
        {loading ? (
          <div className="p-4 space-y-4">
            {[1,2,3,4,5].map(i => (
              <div key={i} className="flex items-center gap-4">
                <Skeleton className="h-4 w-24 bg-[#1d2230]" />
                <Skeleton className="h-4 w-32 bg-[#1d2230]" />
                <Skeleton className="h-6 w-36 bg-[#1d2230] rounded-full" />
                <Skeleton className="h-4 w-24 bg-[#1d2230]" />
              </div>
            ))}
          </div>
        ) : filteredLogs.length > 0 ? (
          <>
            <Table data-testid="audit-table">
              <TableHeader>
                <TableRow className="border-b border-[#252a37] bg-[#1d2230]/50">
                  <TableHead className="text-xs uppercase text-[#8a93a3]">Time</TableHead>
                  <TableHead className="text-xs uppercase text-[#8a93a3]">Actor</TableHead>
                  <TableHead className="text-xs uppercase text-[#8a93a3]">Action</TableHead>
                  <TableHead className="text-xs uppercase text-[#8a93a3]">Target</TableHead>
                  <TableHead className="text-xs uppercase text-[#8a93a3] text-right">Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLogs.map((log, idx) => (
                  <TableRow
                    key={log.id || idx}
                    className="border-b border-[#252a37] hover:bg-[#1d2230]/30 transition-colors"
                  >
                    <TableCell className="font-mono text-xs text-[#8a93a3] whitespace-nowrap">
                      {log.at ? new Date(log.at).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="h-7 w-7 rounded-full bg-[#1d2230] flex items-center justify-center text-[#f5c518] text-xs font-semibold flex-shrink-0">
                          {log.actor_name?.[0]?.toUpperCase() || 'S'}
                        </div>
                        <span className="text-[#f0f2f7] text-sm">{log.actor_name || 'System'}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <ActionBadge action={log.action} />
                    </TableCell>
                    <TableCell className="text-[#8a93a3] text-sm">
                      {log.target?.kind && (
                        <span className="capitalize">{log.target.kind}</span>
                      )}
                      {log.target?.id && (
                        <span className="text-[#8a93a3]/50 text-xs ml-1">
                          ({log.target.id.slice(-8)})
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedLog(log)}
                        className="text-[#8a93a3] hover:text-[#f0f2f7] hover:bg-[#1d2230]"
                        data-testid="audit-log-detail-button"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            <div className="flex items-center justify-between p-4 border-t border-[#252a37]" data-testid="audit-pagination">
              <p className="text-sm text-[#8a93a3]">
                {total} total events
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline" size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="border-[#252a37] text-[#f0f2f7] hover:bg-[#1d2230] disabled:opacity-50"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-[#8a93a3] px-2">Page {page}</span>
                <Button
                  variant="outline" size="sm"
                  onClick={() => { setPage(p => p + 1); fetchLogs(true); }}
                  disabled={filteredLogs.length < LOG_PAGE_SIZE}
                  className="border-[#252a37] text-[#f0f2f7] hover:bg-[#1d2230] disabled:opacity-50"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <CardContent className="p-12 text-center">
            <FileText className="h-12 w-12 text-[#8a93a3] mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[#f0f2f7] mb-2">
              {search ? 'No logs found' : 'No audit logs yet'}
            </h3>
            <p className="text-[#8a93a3]">
              {search ? 'Try adjusting your search' : 'Activity will be logged here as you use the platform'}
            </p>
          </CardContent>
        )}
      </Card>

      {/* Detail Dialog */}
      <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <DialogContent className="bg-[#161a26] border-[#252a37] max-w-2xl" data-testid="audit-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-[#f0f2f7]">
              <FileText className="h-5 w-5 text-[#f5c518]" />
              Audit Event Details
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[420px]">
            {selectedLog && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-[#8a93a3] mb-1">Action</p>
                    <ActionBadge action={selectedLog.action} />
                  </div>
                  <div>
                    <p className="text-xs text-[#8a93a3] mb-1">Timestamp</p>
                    <p className="text-sm text-[#f0f2f7] font-mono">
                      {selectedLog.at ? new Date(selectedLog.at).toLocaleString() : '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-[#8a93a3] mb-1">Actor</p>
                    <p className="text-sm text-[#f0f2f7]">{selectedLog.actor_name || 'System'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#8a93a3] mb-1">Actor ID</p>
                    <p className="text-xs text-[#f0f2f7] font-mono">{selectedLog.actor_user_id || '—'}</p>
                  </div>
                </div>

                {selectedLog.target && (
                  <div>
                    <p className="text-xs text-[#8a93a3] mb-1">Target</p>
                    <div className="bg-[#1d2230] p-3 rounded-lg flex items-center gap-3">
                      <Badge className="bg-[#252a37] text-[#8a93a3] border border-[#252a37] text-xs capitalize">
                        {selectedLog.target.kind}
                      </Badge>
                      <code className="text-xs text-[#f5c518] font-mono">{selectedLog.target.id}</code>
                    </div>
                  </div>
                )}

                {selectedLog.after && Object.keys(selectedLog.after).length > 0 && (
                  <div>
                    <p className="text-xs text-[#8a93a3] mb-1">Changes / After</p>
                    <pre className="text-xs text-[#f0f2f7] bg-[#1d2230] p-3 rounded-lg overflow-auto font-mono max-h-[200px]">
                      {JSON.stringify(selectedLog.after, null, 2)}
                    </pre>
                  </div>
                )}

                <div>
                  <p className="text-xs text-[#8a93a3] mb-1">Event ID</p>
                  <code className="text-xs text-[#8a93a3] font-mono">{selectedLog.id}</code>
                </div>
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
